// src/components/cash/LendingTab.js
import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

// Reuse custom components from BankPay.js
const Button = ({ children, onClick, disabled, style }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      background: 'transparent',
      border: '2px solid #FFD700',
      color: '#FFD700',
      padding: '10px 20px',
      borderRadius: '5px',
      cursor: 'pointer',
      fontWeight: 'bold',
      width: 'auto',
      ...(disabled && { opacity: 0.5, cursor: 'not-allowed' }),
      ...style,
    }}
  >
    {children}
  </button>
);

const Input = ({ placeholder, value, onChange, type, min, step, style }) => (
  <input
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    type={type}
    min={min}
    step={step}
    style={{
      width: '100%',
      padding: '10px',
      border: '1px solid #FFD700',
      borderRadius: '5px',
      background: '#111',
      color: '#FFD700',
      ...style,
    }}
  />
);

const Select = ({ children, value, onValueChange }) => (
  <select
    value={value}
    onChange={(e) => onValueChange(e.target.value)}
    style={{ width: '100%', padding: '10px', border: '1px solid #FFD700', borderRadius: '5px', background: '#111', color: '#FFD700' }}
  >
    {children}
  </select>
);

export const LendingTab = ({ userAddress, contract, contractData, loading, setLoading, setError }) => {
  const [positions, setPositions] = useState([]);
  const [collateralToken, setCollateralToken] = useState('USDC');
  const [collateralAmount, setCollateralAmount] = useState('');
  const [borrowToken, setBorrowToken] = useState('USDC');
  const [selectedPositionId, setSelectedPositionId] = useState('');
  const [borrowAmount, setBorrowAmount] = useState('');
  const [repayPositionId, setRepayPositionId] = useState('');
  const [repayAmount, setRepayAmount] = useState('');
  const [loanRequestData, setLoanRequestData] = useState({
    amount: '',
    collateralAmount: '',
    interestRate: '6.45', // Default interest rate
    duration: '30' // Default 30 days
  });

  // Fetch user's lending positions from contract
  const fetchPositions = async () => {
    if (!contract || !userAddress) return;

    setLoading(true);
    try {
      const loanIds = await contract.getUserLoanIds(userAddress);
      const positionPromises = loanIds.map(async (loanId) => {
        try {
          const loan = await contract.getActiveLoan(loanId);
          return {
            id: loan.loanId.toString(),
            collateralToken: 'USDC',
            collateralAmount: ethers.formatUnits(loan.collateralAmount, 6),
            borrowToken: 'USDC',
            borrowAmount: ethers.formatUnits(loan.principalAmount, 6),
            collateralizationRatio: loan.collateralAmount > 0
              ? (parseFloat(ethers.formatUnits(loan.collateralAmount, 6)) / parseFloat(ethers.formatUnits(loan.principalAmount, 6)) * 100).toFixed(1)
              : '0',
            riskScore: loan.totalOwed > loan.principalAmount ? 'High' : 'Safe',
            borrowRate: (Number(loan.interestRate) / 100).toFixed(2),
            status: loan.isRepaid ? 'repaid' : 'active',
            createdAt: new Date(Number(loan.startTime) * 1000).toISOString(),
            totalOwed: ethers.formatUnits(loan.totalOwed, 6),
            endTime: Number(loan.endTime)
          };
        } catch (error) {
          console.error(`Error loading loan ${loanId}:`, error);
          return null;
        }
      });

      const positionsResult = await Promise.all(positionPromises);
      setPositions(positionsResult.filter(pos => pos !== null));
    } catch (error) {
      console.error('Failed to fetch positions from contract:', error);
      setError('Failed to load lending positions from contract');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userAddress && contract) {
      fetchPositions();
    }
  }, [userAddress, contract]);

  // Handle collateral deposit (USDC approval and deposit)
  const handleCollateralDeposit = async () => {
    if (!contract || !collateralAmount || parseFloat(collateralAmount) <= 0) {
      setError('Please enter a valid collateral amount');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const amountInUnits = ethers.parseUnits(collateralAmount, 6);

      // First, we need to approve USDC for the contract
      const usdcAddress = await contract.USDC();
      const usdcAbi = ['function approve(address spender, uint256 amount) returns (bool)'];
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const usdcContract = new ethers.Contract(usdcAddress, usdcAbi, signer);

      // Approve USDC
      const approveTx = await usdcContract.approve(contract.target, amountInUnits);
      await approveTx.wait();

      // Then deposit collateral
      const depositTx = await contract.deposit(amountInUnits);
      alert(`✅ Contract collateral deposit submitted! Transaction: ${depositTx.hash}`);

      await depositTx.wait();
      alert(`✅ Contract collateral deposit confirmed!`);

      // Refresh positions
      await fetchPositions();
      setCollateralAmount('');

    } catch (error) {
      console.error('Contract collateral deposit error:', error);
      setError(`Collateral deposit failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle loan request
  const handleLoanRequest = async () => {
    if (!contract || !loanRequestData.amount || parseFloat(loanRequestData.amount) <= 0) {
      setError('Please enter a valid loan amount');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const amountInUnits = ethers.parseUnits(loanRequestData.amount, 6);
      const collateralInUnits = ethers.parseUnits(loanRequestData.collateralAmount || '0', 6);
      const interestRateInBps = ethers.parseUnits(loanRequestData.interestRate, 2); // Convert to basis points
      const durationInSeconds = parseInt(loanRequestData.duration) * 24 * 60 * 60; // Convert days to seconds

      const tx = await contract.requestLoan(amountInUnits, collateralInUnits, interestRateInBps, durationInSeconds);
      alert(`✅ Loan request submitted! Transaction: ${tx.hash}`);

      await tx.wait();
      alert(`✅ Loan request confirmed!`);

      // Refresh positions
      await fetchPositions();
      setLoanRequestData({ amount: '', collateralAmount: '', interestRate: '6.45', duration: '30' });

    } catch (error) {
      console.error('Loan request error:', error);
      setError(`Loan request failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle loan repayment
  const handleRepay = async () => {
    if (!contract || !repayAmount || parseFloat(repayAmount) <= 0 || !repayPositionId) {
      setError('Please enter a valid repay amount and select a position');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const amountInUnits = ethers.parseUnits(repayAmount, 6);

      // Approve USDC first if needed
      const usdcAddress = await contract.USDC();
      const usdcAbi = ['function approve(address spender, uint256 amount) returns (bool)'];
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const usdcContract = new ethers.Contract(usdcAddress, usdcAbi, signer);

      const approveTx = await usdcContract.approve(contract.target, amountInUnits);
      await approveTx.wait();

      // Then repay loan
      const repayTx = await contract.repayLoan(repayPositionId);
      alert(`✅ Loan repayment submitted! Transaction: ${repayTx.hash}`);

      await repayTx.wait();
      alert(`✅ Loan repayment confirmed!`);

      // Refresh positions
      await fetchPositions();
      setRepayAmount('');
      setRepayPositionId('');

    } catch (error) {
      console.error('Loan repayment error:', error);
      setError(`Loan repayment failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle emergency withdrawal
  const handleEmergencyWithdraw = async () => {
    if (!contract) {
      setError('Contract not initialized');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const tx = await contract.emergencyWithdraw();
      alert(`✅ Emergency withdrawal submitted! Transaction: ${tx.hash}`);

      await tx.wait();
      alert(`✅ Emergency withdrawal confirmed!`);

      // Refresh positions
      await fetchPositions();

    } catch (error) {
      console.error('Emergency withdrawal error:', error);
      setError(`Emergency withdrawal failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for display
  const totalCollateral = positions.reduce((sum, pos) => sum + parseFloat(pos.collateralAmount || 0), 0);
  const totalBorrowed = positions.reduce((sum, pos) => sum + parseFloat(pos.borrowAmount || 0), 0);
  const totalOwed = positions.reduce((sum, pos) => sum + parseFloat(pos.totalOwed || 0), 0);
  const averageRatio = positions.length > 0
    ? positions.reduce((sum, pos) => sum + parseFloat(pos.collateralizationRatio || 0), 0) / positions.length
    : 0;

  const activePositions = positions.filter(p => p.status === 'active');
  const repaidPositions = positions.filter(p => p.status === 'repaid');

  return (
    <div className="max-w-2xl mx-auto bg-[#111] rounded-lg shadow-md p-6" style={{ color: '#FFD700' }}>
      {loading && <div className="text-center" style={{ color: '#FFD700', marginBottom: '20px' }}>Loading contract data...</div>}

      <h2 className="text-2xl font-bold mb-4">Lending Overview</h2>

      {/* Contract Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
        padding: '15px',
        background: 'rgba(255, 215, 0, 0.1)',
        border: '1px solid #FFD700',
        borderRadius: '5px',
        marginBottom: '20px'
      }}>
        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Total Collateral:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>${totalCollateral.toFixed(2)}</div>

        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Total Borrowed:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>${totalBorrowed.toFixed(2)}</div>

        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Total Owed:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>${totalOwed.toFixed(2)}</div>

        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Avg. Ratio:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>{averageRatio.toFixed(1)}%</div>

        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Active Positions:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>{activePositions.length}</div>

        <div style={{ textAlign: 'right', paddingRight: '10px', color: '#ccc' }}>Repaid Positions:</div>
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>{repaidPositions.length}</div>
      </div>

      {/* Deposit Collateral */}
      <div style={{ marginBottom: '30px' }}>
        <h3 className="text-xl font-bold mb-4">Deposit Collateral</h3>
        <div style={{ display: 'grid', gap: '15px' }}>
          <div>
            <label className="block text-sm mb-2">Collateral Amount (USDC)</label>
            <Input
              type="number"
              value={collateralAmount}
              onChange={(e) => setCollateralAmount(e.target.value)}
              placeholder="Enter USDC amount"
              min="0"
              step="0.01"
            />
          </div>
          <Button onClick={handleCollateralDeposit} disabled={loading || !collateralAmount}>
            {loading ? 'Processing...' : 'Deposit Collateral'}
          </Button>
        </div>
        {collateralAmount && (
          <div style={{ marginTop: '15px', padding: '15px', border: '1px solid #FFD700', borderRadius: '8px', background: '#111' }}>
            <p className="text-sm">Fee: {(parseFloat(collateralAmount || 0) * parseFloat(contractData.feePercent || 0) / 100).toFixed(4)} USDC</p>
            <p className="text-sm text-green-600 mt-1">Collateral Ratio: {contractData.collateralRatio}% required</p>
          </div>
        )}
      </div>

      {/* Request Loan */}
      <div style={{ marginBottom: '30px' }}>
        <h3 className="text-xl font-bold mb-4">Request Loan</h3>
        <div style={{ display: 'grid', gap: '15px' }}>
          <div>
            <label className="block text-sm mb-2">Loan Amount (USDC)</label>
            <Input
              type="number"
              value={loanRequestData.amount}
              onChange={(e) => setLoanRequestData(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="Enter loan amount"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm mb-2">Collateral Amount (USDC)</label>
            <Input
              type="number"
              value={loanRequestData.collateralAmount}
              onChange={(e) => setLoanRequestData(prev => ({ ...prev, collateralAmount: e.target.value }))}
              placeholder="Enter collateral amount"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm mb-2">Interest Rate (%)</label>
            <Input
              type="number"
              value={loanRequestData.interestRate}
              onChange={(e) => setLoanRequestData(prev => ({ ...prev, interestRate: e.target.value }))}
              placeholder="Enter interest rate"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm mb-2">Duration (Days)</label>
            <Input
              type="number"
              value={loanRequestData.duration}
              onChange={(e) => setLoanRequestData(prev => ({ ...prev, duration: e.target.value }))}
              placeholder="Enter duration in days"
              min="1"
              step="1"
            />
          </div>
          <Button onClick={handleLoanRequest} disabled={loading || !loanRequestData.amount}>
            {loading ? 'Processing...' : 'Request Loan'}
          </Button>
        </div>
      </div>

      {/* Repay Loan */}
      <div style={{ marginBottom: '30px' }}>
        <h3 className="text-xl font-bold mb-4">Repay Loan</h3>
        <div style={{ display: 'grid', gap: '15px' }}>
          <div>
            <label className="block text-sm mb-2">Select Loan to Repay</label>
            <Select value={repayPositionId} onValueChange={setRepayPositionId}>
              <option value="">Choose loan...</option>
              {activePositions.map((position) => (
                <option key={position.id} value={position.id}>
                  Loan #{position.id} - Owed: ${parseFloat(position.totalOwed).toFixed(2)} USDC
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm mb-2">Repay Amount (USDC)</label>
            <Input
              type="number"
              value={repayAmount}
              onChange={(e) => setRepayAmount(e.target.value)}
              placeholder="Enter repay amount"
              min="0"
              step="0.01"
            />
          </div>
          <Button onClick={handleRepay} disabled={loading || !repayAmount || !repayPositionId}>
            {loading ? 'Processing...' : 'Repay Loan'}
          </Button>
        </div>
      </div>

      {/* Emergency Withdrawal */}
      <div style={{ marginBottom: '30px' }}>
        <h3 className="text-xl font-bold mb-4">Emergency Withdrawal</h3>
        <p className="text-sm mb-4" style={{ color: '#ccc' }}>
          Use this only in emergency situations to withdraw all collateral. This may incur penalties.
        </p>
        <Button onClick={handleEmergencyWithdraw} disabled={loading} style={{ background: 'rgba(255, 0, 0, 0.2)' }}>
          {loading ? 'Processing...' : 'Emergency Withdraw'}
        </Button>
      </div>

      {/* Lending Positions */}
      <div>
        <h3 className="text-xl font-bold mb-4">Your Lending Positions</h3>
        {loading ? (
          <div className="text-center">Loading positions...</div>
        ) : positions.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No loan positions found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #FFD700' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Loan ID</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Collateral</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Borrowed</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Owed</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Ratio</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Rate</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((position) => (
                  <tr key={position.id} style={{ borderBottom: '1px solid #FFD700' }}>
                    <td style={{ padding: '10px' }}>#{position.id}</td>
                    <td style={{ padding: '10px' }}>${parseFloat(position.collateralAmount).toFixed(2)}</td>
                    <td style={{ padding: '10px' }}>${parseFloat(position.borrowAmount).toFixed(2)}</td>
                    <td style={{ padding: '10px' }}>${parseFloat(position.totalOwed).toFixed(2)}</td>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        color: parseFloat(position.collateralizationRatio) > 200 ? '#00FF00' :
                               parseFloat(position.collateralizationRatio) > 150 ? '#FFA500' : '#FF0000',
                      }}>
                        {position.collateralizationRatio}%
                      </span>
                    </td>
                    <td style={{ padding: '10px' }}>{position.borrowRate}%</td>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        background: position.status === 'active' ? '#00FF00' : '#ccc',
                        color: '#111',
                      }}>
                        {position.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px' }}>
                      {position.endTime ? new Date(position.endTime * 1000).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
